import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ref, onValue, off } from 'firebase/database';
import { db } from '../../firebase/config';
import './Home.css';

interface Testimonial {
  id: string;
  rating: number;
  text: string;
  author: string;
  title: string;
  initials: string;
  createdAt?: number;
}

const Home: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Firebase'den yorumları çek (Realtime)
  useEffect(() => {
    const testimonialsRef = ref(db, 'testimonials');
    
    const unsubscribe = onValue(testimonialsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const loadedTestimonials: Testimonial[] = [];
        
        Object.keys(data).forEach((key) => {
          loadedTestimonials.push({
            id: key,
            ...data[key]
          } as Testimonial);
        });
        
        // Tarihe göre sırala (en yeni önce)
        loadedTestimonials.sort((a, b) => {
          const timeA = a.createdAt || 0;
          const timeB = b.createdAt || 0;
          return timeB - timeA;
        });
        
        setTestimonials(loadedTestimonials);
      } else {
        setTestimonials([]);
      }
      setIsLoading(false);
    }, (error) => {
      console.error('Yorumlar yüklenirken hata:', error);
      setIsLoading(false);
    });

    return () => {
      off(testimonialsRef);
    };
  }, []);

  // Her seferinde 1 yorum kayacak, ama ekranda 3 yorum görünecek
  // Döngüsel kaydırma için
  const nextSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    if (testimonials.length === 0) return;
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Görüntülenecek yorumları al (her zaman 3 yorum, döngüsel)
  const getVisibleTestimonials = () => {
    if (testimonials.length === 0) return [];
    const visible = [];
    const count = Math.min(3, testimonials.length);
    for (let i = 0; i < count; i++) {
      const index = (currentSlide + i) % testimonials.length;
      visible.push(testimonials[index]);
    }
    return visible;
  };

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-background">
          <img src="/images/hero-1.jpeg" alt="Prefabrik Yapı" className="hero-bg-image" loading="eager" />
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              Modern Prefabrik
              <span className="highlight"> Yapı Çözümleri</span>
            </h1>
            <p className="hero-subtitle">
              Hayallerinizdeki yapıyı hızlı, kaliteli ve ekonomik bir şekilde 
              gerçeğe dönüştürüyoruz. Profesyonel ekibimiz ve yılların deneyimi 
              ile yanınızdayız.
            </p>
            <div className="hero-buttons">
              <Link to="/hizmetlerimiz" className="btn btn-primary">
                Hizmetlerimiz
              </Link>
              <Link to="/iletisim" className="btn btn-secondary">
                İletişime Geç
              </Link>
            </div>
          </div>
        </div>
        <div className="hero-scroll">
          <span>Keşfet</span>
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features section">
        <div className="container">
          <div className="features-header">
            <h2 className="section-title">Neden Bizi Seçmelisiniz?</h2>
            <p className="features-subtitle">
              Yılların deneyimi, modern teknoloji ve müşteri odaklı yaklaşımımızla 
              prefabrik yapı sektöründe fark yaratıyoruz.
            </p>
          </div>
          <div className="features-grid">
            <div className="feature-card feature-card-large">
              <div className="feature-image-wrapper">
                <img src="/images/service-1.jpeg" alt="Hızlı Kurulum" loading="lazy" />
                <div className="feature-overlay"></div>
              </div>
              <div className="feature-content">
                <div className="feature-number">01</div>
                <h3 className="feature-title">Hızlı Kurulum</h3>
                <p className="feature-description">
                  Prefabrik yapılarımız kısa sürede kurulur, zaman ve maliyet tasarrufu sağlar. 
                  Geleneksel yapılara göre %60 daha hızlı teslimat.
                </p>
                <div className="feature-stats">
                  <span className="stat-item">2-4 Hafta</span>
                  <span className="stat-item">%60 Hızlı</span>
                </div>
              </div>
            </div>
            <div className="feature-card feature-card-medium">
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <img src="/images/service-2.jpeg" alt="Yüksek Kalite" loading="lazy" />
                </div>
                <div className="feature-number-small">02</div>
              </div>
              <h3 className="feature-title">Yüksek Kalite</h3>
              <p className="feature-description">
                Modern teknoloji ve kaliteli malzemelerle üretilen dayanıklı yapılar. 
                ISO sertifikalı üretim süreçlerimiz.
              </p>
            </div>
            <div className="feature-card feature-card-medium">
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <img src="/images/service-3.jpeg" alt="Ekonomik Çözümler" loading="lazy" />
                </div>
                <div className="feature-number-small">03</div>
              </div>
              <h3 className="feature-title">Ekonomik Çözümler</h3>
              <p className="feature-description">
                Geleneksel yapılara göre daha uygun fiyatlı, uzun vadede tasarruf sağlayan çözümler.
              </p>
            </div>
            <div className="feature-card feature-card-medium">
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <img src="/images/project-1.jpeg" alt="Özel Tasarım" loading="lazy" />
                </div>
                <div className="feature-number-small">04</div>
              </div>
              <h3 className="feature-title">Özel Tasarım</h3>
              <p className="feature-description">
                İhtiyaçlarınıza özel tasarlanmış, estetik ve fonksiyonel yapı çözümleri.
              </p>
            </div>
            <div className="feature-card feature-card-medium">
              <div className="feature-icon-wrapper">
                <div className="feature-icon">
                  <img src="/images/project-2.jpeg" alt="Çevre Dostu" loading="lazy" />
                </div>
                <div className="feature-number-small">05</div>
              </div>
              <h3 className="feature-title">Çevre Dostu</h3>
              <p className="feature-description">
                Sürdürülebilir malzemeler ve enerji verimli tasarımlarla çevreye duyarlı yapılar.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="services-preview section">
        <div className="container">
          <h2 className="section-title">Hizmetlerimiz</h2>
          <div className="services-grid">
            <div className="service-card">
              <div className="service-image">
                <img src="/images/service-1.jpeg" alt="Prefabrik Evler" loading="lazy" />
              </div>
              <h3 className="service-card-title">Prefabrik Evler</h3>
              <p className="service-card-description">
                Modern ve konforlu yaşam alanları için prefabrik ev çözümleri.
              </p>
              <Link to="/hizmetlerimiz" className="service-link">
                Detaylar →
              </Link>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/service-2.jpeg" alt="Prefabrik Villalar" loading="lazy" />
              </div>
              <h3 className="service-card-title">Prefabrik Villalar</h3>
              <p className="service-card-description">
                Lüks ve şık villalar, özel tasarım ve yüksek kalite standartları.
              </p>
              <Link to="/hizmetlerimiz" className="service-link">
                Detaylar →
              </Link>
            </div>
            <div className="service-card">
              <div className="service-image">
                <img src="/images/service-3.jpeg" alt="Modül Ev" loading="lazy" />
              </div>
              <h3 className="service-card-title">Modül Ev</h3>
              <p className="service-card-description">
                Modern ve konforlu modül ev çözümleri. Hızlı kurulumlu ve ekonomik yaşam alanları.
              </p>
              <Link to="/hizmetlerimiz" className="service-link">
                Detaylar →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials section">
        <div className="container">
          <h2 className="section-title">Müşteri Yorumları</h2>
          <p className="testimonials-subtitle">
            Müşterilerimizin deneyimleri bizim en büyük motivasyonumuz
          </p>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              Yorumlar yükleniyor...
            </div>
          ) : testimonials.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              Henüz yorum bulunmamaktadır.
            </div>
          ) : (
            <>
              <div className="testimonials-carousel-wrapper">
                {testimonials.length > 3 && (
                  <button 
                    className="carousel-arrow carousel-arrow-left" 
                    onClick={prevSlide}
                    aria-label="Önceki yorumlar"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 18l-6-6 6-6"/>
                    </svg>
                  </button>
                )}
                <div className="testimonials-carousel">
                  <div className="testimonials-slide">
                    {getVisibleTestimonials().map((testimonial) => (
                      <div key={testimonial.id} className="testimonial-card">
                        <div className="testimonial-rating">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <span key={i} className="star">★</span>
                          ))}
                        </div>
                        <p className="testimonial-text">
                          "{testimonial.text}"
                        </p>
                        <div className="testimonial-author">
                          <div className="author-avatar">
                            <span>{testimonial.initials || '??'}</span>
                          </div>
                          <div className="author-info">
                            <h4 className="author-name">{testimonial.author}</h4>
                            <p className="author-title">{testimonial.title}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {testimonials.length > 3 && (
                  <button 
                    className="carousel-arrow carousel-arrow-right" 
                    onClick={nextSlide}
                    aria-label="Sonraki yorumlar"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18l6-6-6-6"/>
                    </svg>
                  </button>
                )}
              </div>
              {testimonials.length > 3 && (
                <div className="carousel-dots">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      className={`carousel-dot ${currentSlide === index ? 'active' : ''}`}
                      onClick={() => setCurrentSlide(index)}
                      aria-label={`Slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Hayalinizdeki Yapıyı Birlikte Gerçekleştirelim</h2>
            <p className="cta-description">
              Profesyonel ekibimiz size en uygun çözümü sunmak için hazır. 
              Hemen iletişime geçin ve ücretsiz keşif fırsatından yararlanın.
            </p>
            <Link to="/iletisim" className="btn btn-primary">
              Ücretsiz Teklif Al
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
